import { memo, useMemo, useState, useEffect } from "react";
import { CardMedia } from "../../components/common/card-media/cardMedia";
import { BlogPageTranslations } from "../../model/blog";
import { getUniqueRandomIndices } from "../../utils/random-index.utils";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/splide/css/core";
import { useRouter } from "next/router";

interface Props {
  title: string;
  blogContent: BlogPageTranslations[];
}

export const Recommendations = memo<Props>(({ blogContent, title }) => {
  const options = useMemo(
    () => ({
      rewindByDrag: true,
      speed: 600,
      perPage: 1,
      perMove: 1,
      lazyLoad: false,
      type: "loop",
      breakpoints: {
        768: {
          type: "loop",
          fixedWidth: "320px",
          perPage: 1.3,
          perMove: 1,
        },
      },
    }),
    []
  );
  const router = useRouter();
  const { locale } = router;

  const contentLocale = locale === "ru" ? "ru" : "en";

  const [filteredContent, setFilteredContent] = useState<
    BlogPageTranslations[]
  >([]);

  useEffect(() => {
    const currentContent = blogContent.filter(
      (item) => item.__i18n_lang === contentLocale
    );

    const uniqueIndices = getUniqueRandomIndices(currentContent.length, 3);

    setFilteredContent(uniqueIndices.map((index) => currentContent[index]));
  }, [blogContent, contentLocale]);

  const renderCardMedia = (content: BlogPageTranslations) => {
    const englishVersionId =
      locale === "ru" ? `${content._id}__i18n_en` : content._id;

    const englishVersionContent = blogContent.find(
      (item) => item._id === englishVersionId
    );

    let englishVersionSlug = "";
    if (englishVersionContent) {
      englishVersionSlug = englishVersionContent.slug.current;
    }

    return (
      <CardMedia
        key={content._id}
        className="lg:h-full"
        slug={englishVersionSlug}
        blogContent={content}
      />
    );
  };

  return (
    <div className="max-[1360px] m-auto">
      <div className="pl-10 pr-10 text-[32px] leading-[112%] lg:text-[58px] pt-30 pb-5 lg:pt-[100px] lg:pb-24 lg:leading-[100%] text-center w-full">
        {title}
      </div>
      <div className="hidden lg:grid grid-cols-3 lg:mr-20 lg:ml-12 xxxxl:pl-[100px] xxxxl:pr-[100px] bigSize:pl-[240px] bigSize:pr-[240px]">
        {filteredContent.map(renderCardMedia)}
      </div>
      <div className="lg:hidden" aria-label="Blog recommendation banner">
        <Splide options={options} className="w-auto h-full pb-20 lg:pb-0">
          {filteredContent.map((content) => (
            <SplideSlide key={content._id}>
              {renderCardMedia(content)}
            </SplideSlide>
          ))}
        </Splide>
      </div>
    </div>
  );
});
